// @ts-strict-ignore
/* globals PROJECT_3_MIXPANEL_TOKEN */
import produce from 'immer';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import {Component} from 'panel';

import {parseUri} from 'common/util';
import {getCookie, safeSetCookie} from 'common/util/cookie';
import {getLocation} from 'common/util/url';

import {reporter} from './error-reporting';
import {initMixpanel} from './tracking-setup';
import {getTrackingData} from './queries/app-api/project-tracking-data-client';
import {getCustomAlertIdFromUrl, getMpSourceFromUrl, getProjectId, getWorkspaceId, isEmbedMode} from './util/index';
import {userCookieUtils} from './util/user-identity-from-cookie';
import {currentProjectStore} from './stores/project-details';
import {ProjectFeatureFlag, TRACKING_FEATURE_FLAGS} from './constants';

export interface TrackingOptions {
  includeReportProperties?: boolean;
  includeReportPrefix?: boolean;
}

interface SdkEventData {
  event: string;
  properties: Obj;
}

export interface UserTrackingData {
  projectId: number;
  userId: number;
  userEmail: string;
  userName: string;
}

type TrackListenerFn = (ev) => void;
type AnyComponent = Component<any, any, any, any, any>;

/**
 * Set of utility methods to capture and process all
 * report specific event tracking
 */
export class ReportTracker {
  /** Function which returns additional properties to track. */
  // TODO: rename this, this is now just a list of additional props that
  // this tracker appends
  public declare appPersistentProperties?: () => any;
  /** Set of unprefixed event names allowed to be sent to project 3. */
  public declare productionEvents?: Set<string>;
  /** Tag events with this prefix before sending to project 3. */
  public declare productionPrefix: string;
  private trackListenerByComponent = new Map<AnyComponent, TrackListenerFn>();

  public declare userTrackingData: UserTrackingData;
  public declare mixpanel: any;

  /**
   * @param appPersistentProperties function to inject properties at track time
   *
   * @see member documentation for more details
   */
  constructor({
    appPersistentProperties = () => {
      /* noop */
    },
    productionEvents = new Set<string>(),
    productionPrefix,
    userTrackingData,
  }: {
    appPersistentProperties?: () => any;
    productionEvents?: Set<string>;
    productionPrefix?: string;
    userTrackingData: UserTrackingData;
  }) {
    this.mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
    this.appPersistentProperties = appPersistentProperties;
    this.productionEvents = productionEvents;
    this.productionPrefix = productionPrefix;
    this.userTrackingData = userTrackingData;

    this.identifyUser();
  }

  /**
   * Handles track events generated by components.
   * Each component triggering this event is responsible
   * for providing an eventName and props to track
   */
  public onAppTrackEvent(ev) {
    const {eventName, props} = ev.detail;
    ev.stopPropagation();
    this.track(eventName, props || {});
  }

  /**
   * Convenience util for a component to automatically listen for and handle track events
   */
  public listenForTracking(component: AnyComponent) {
    const scopedListener = (ev) => this.onAppTrackEvent(ev);
    this.stopListenForTracking(component);
    this.trackListenerByComponent.set(component, scopedListener);
    component.addEventListener(`track`, scopedListener);
    component.onDisconnected(() => this.stopListenForTracking(component));
  }

  public stopListenForTracking(component: AnyComponent) {
    const listener = this.trackListenerByComponent.get(component);
    this.trackListenerByComponent.delete(component);
    if (listener) {
      component.removeEventListener(`track`, listener);
    }
  }

  /**
   * Tracks an event and props on the app's mixpanel project
   * and checks if the event will be sent to the Project 3
   * mixpanel project with an optional prefix
   */
  public track(event: string, properties: Obj = {}, trackingOptions?: TrackingOptions) {
    const options = {
      includeReportProperties: true,
      includeReportPrefix: true,
      ...trackingOptions,
    };

    properties = this.populateEventProperties(properties, options.includeReportProperties);

    // Send bubbled `track` events to project 3 from components and widgets
    // defining their own event prefix(mp-table, mp-query-builder, etc...)
    // TODO (pablofierro): consolidate all track events to use `util/dom#emitTrackEvent`
    // to figure out when an event has a prefix instead of inspecting the event name
    const isPrefixedEvent = event.startsWith(`[`);

    if (this.productionEvents.has(event) || isPrefixedEvent) {
      const eventName =
        !isPrefixedEvent && this.productionPrefix && options.includeReportPrefix
          ? `[${this.productionPrefix}] ${event}`
          : event;

      this.mixpanel.track(eventName, properties);
    }
  }

  /**
   * Registers super properties for all projects
   */
  public register(properties) {
    this.mixpanel.register(properties);
  }

  public trackLinks(
    query: any,
    event: string,
    properties: Obj,
    options: TrackingOptions = {includeReportPrefix: true},
  ) {
    if (this.productionEvents.has(event)) {
      const eventName =
        this.productionPrefix && options.includeReportPrefix ? `[${this.productionPrefix}] ${event}` : event;
      this.mixpanel.track_links(query, eventName, properties);
    }
  }

  public identifyUser() {
    return this.userTrackingData;
  }

  private populateEventProperties(properties: Obj, includeReportProperties = false) {
    const {userEmail, projectId} = this.userTrackingData;

    return {
      ...(includeReportProperties ? this.appPersistentProperties() : {}),
      ...properties,
      Email: userEmail,
      project_id: projectId,
    };
  }
}

export function registerUnpersisted(mpInstance, props) {
  // first remove from persistence if it was put there previously
  // TODO 2020-09-14 after we've run this way for long enough that
  // people no longer have these values in localStorage, we can remove
  // this block
  for (const prop of Object.keys(props)) {
    mpInstance.unregister(prop);
  }
  mpInstance.register(props, {persistent: false});
}

// get viewport dimensions: http://stackoverflow.com/questions/1248081/get-the-browser-viewport-dimensions-with-javascript
function registerViewportDimensions() {
  registerUnpersisted(mixpanel, {
    'Viewport Width': Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
  });
  registerUnpersisted(mixpanel, {
    'Viewport Height': Math.max(document.documentElement.clientHeight, window.innerHeight || 0),
  });
}

function setUserId(userId, skipIdentify = false) {
  if (!userId) {
    return;
  }

  if (!skipIdentify) {
    mixpanel.identify(String(userId));
    // set cross-domain cookie containing distinct id so that tracking on our subdomains
    // (helpcenter, community, blog, etc.) can be associated with the correct user
    safeSetCookie(`mp_distinct_id`, String(userId), 365, true);
  }
  registerUnpersisted(mixpanel, {user_id: userId});
}

export function setReferrerSuperpropsAndBaseTracking({skipIdentify = false, userId = null} = {}) {
  try {
    setUserId(userId, skipIdentify);

    const originReferrer = getCookie(`mp__origin_referrer`);
    const originDomain = originReferrer && parseUri(originReferrer).host;
    const originProps = pickBy(
      {
        origin: getCookie(`mp__origin`),
        origin_referrer: originReferrer,
        origin_domain: originDomain,
      },
      Boolean,
    );
    if (!isEmpty(originProps)) {
      mixpanel.register_once(originProps);
    }

    // we only want to set landing page url for users who have never been to Mixpanel before.
    // we have to use this 'push' functionality so we can access the cookie to determine whether or not
    // to actually set the property
    mixpanel.push(function (this: any) {
      if (!(`first touch landing page url` in this.persistence.props)) {
        const firstTouchInfo = {'first touch landing page url': getLocation().href};
        this.register_once(firstTouchInfo);
      }
    });

    // some screen/viewport tracking
    const retinaProp = {'Device Pixel Ratio (is it retina?)': window.devicePixelRatio};
    registerUnpersisted(mixpanel, retinaProp);
    registerViewportDimensions();
    window.addEventListener(`resize`, debounce(registerViewportDimensions, 500));
  } catch (err) {
    reporter.error(`Error initializing basic tracking:`, err);
  }
}

interface UserBasedTrackingProps {
  firstName: string;
  lastName: string;
  email: string;
  dateJoined: string | Date;
  lastLogin: string | Date;
  accountId?: number;
}
let userBasedTrackingPropsSet = false;
export function setUserBasedTrackingProps(props: UserBasedTrackingProps, {delayed = false} = {}) {
  // prevent double execution in the case of Django-templated report pages,
  // since this helper is also called directly from the template (since it's
  // included from base.html, this code needs to run across more than just
  // report pages, so can't rely on the API-based delayed tracking helpers below)
  if (userBasedTrackingPropsSet) {
    return;
  }
  userBasedTrackingPropsSet = true;

  if (props.accountId) {
    const accountProp = {'Project owner billing account ID': props.accountId};
    if (delayed) {
      // apply retroactively to events that may have been tracked already
      mpSdkSetBeforeSendProps(accountProp);
    } else {
      // not using delayed tracking, just set as regular superprop
      registerUnpersisted(mixpanel, accountProp);
    }
    // set cross-domain cookie containing account id so that events tracked on our subdomains
    // (helpcenter, community, blog, etc.) can be associated with the correct billing account
    safeSetCookie(`mp_account_id`, String(props.accountId), 365, true);
  }
}

// props to be added to all P3 track calls (even retroactively
// for track calls which haven't gone out on network yet)
const mpSdkPropCache = {};
function mpSdkSetBeforeSendProps(props: Obj, eventName = ``) {
  mpSdkPropCache[eventName] = mpSdkPropCache[eventName] || {};
  Object.assign(mpSdkPropCache[eventName], props);
}

function mpSdkBeforeSendEventHandler(eventData: SdkEventData) {
  return produce(eventData, function (data) {
    Object.assign(data.properties, mpSdkPropCache[``], mpSdkPropCache[eventData.event]);
  });
}

/** adds a list of relevant feature flags for all P3 events */
export function setTrackingFeatureFlagProps(flags: ProjectFeatureFlag[] = null) {
  const currentFlags = (mpSdkPropCache[``] && mpSdkPropCache[``][`Project Feature Flags`]) || [];

  // read feature flag status from store
  if (flags === null) {
    flags = TRACKING_FEATURE_FLAGS.filter((flag) => currentProjectStore.value?.hasFeatureFlag(flag));
  }

  flags = Array.from(new Set([...currentFlags, ...flags]));
  mpSdkSetBeforeSendProps({
    'Project Feature Flags': flags,
  });
}

export function setEmbedTrackingProps() {
  if (isEmbedMode()) {
    const embedLocation = document.location.ancestorOrigins?.[document.location.ancestorOrigins.length - 1] ?? ``;
    mpSdkSetBeforeSendProps({
      context: `embed`,
      'Embed Location': embedLocation,
    });
  } else {
    mpSdkSetBeforeSendProps({context: `standard`});
  }
}

export function setMpSourceTrackingProps() {
  const mpSource = getMpSourceFromUrl();
  if (mpSource) {
    mpSdkSetBeforeSendProps({
      mp_source: mpSource,
      ...([`custom_alert`, `anomaly_detection_alert`].includes(mpSource)
        ? {'alert id': getCustomAlertIdFromUrl()}
        : {}),
    });
  }
}

/**
 * Fetch Django-supplied prop data from API, add to the prop cache, and
 * then tell the SDK to start sending over the network (so that these props
 * are also sent with events that were mixpanel.track()ed before the API
 * call finished).
 */
let trackingPropsInitCalled = false;
export async function mpSdkInitTrackingProps() {
  // prevent double execution of this helper, at least during rollout
  // that transitions away from calling it from Django template
  if (trackingPropsInitCalled) {
    return;
  }
  trackingPropsInitCalled = true;

  mixpanel.set_config({
    hooks: {
      before_send_events: mpSdkBeforeSendEventHandler,
    },
  });

  // wrap everything in try/catch so that errors (API or otherwise)
  // don't prevent P3 events from going out at all
  try {
    const projectId = getProjectId();
    const workspaceId = getWorkspaceId();
    mpSdkSetBeforeSendProps({
      project_id: projectId,
      Email: userCookieUtils.getUserIdentityFromCookie()?.email,
    });
    setMpSourceTrackingProps();

    if (projectId) {
      currentProjectStore.fetch().then(() => setTrackingFeatureFlagProps());
      const projectProps = await getTrackingData(projectId, workspaceId);
      // P3-specific superprop logic here comes from:
      // https://github.com/mixpanel/analytics/blob/d982fdef63598af3ffefb2c506d5a9bc9fdf0043/templates/reports/base-new.html#L248-L385
      const {billingCredit, organization, projectAccount, user} = projectProps;
      const {eventsPlan, peoplePlan} = projectAccount;

      setUserId(user.id);
      mpSdkSetBeforeSendProps({Email: user.email});
      setUserBasedTrackingProps(
        {
          ...pick(user, [`firstName`, `lastName`, `email`, `dateJoined`, `lastLogin`]),
          accountId: projectAccount.id,
        },
        {delayed: true},
      );

      mpSdkSetBeforeSendProps({
        'Billing: events/MTU plan ID': eventsPlan.id,
        'Billing: events/MTU plan name': eventsPlan.name,
        'Billing: people plan ID': peoplePlan.id,
        'Billing: people plan name': peoplePlan.name,
        'Billing: events/MTU plan term length': eventsPlan.termLengthInMonths,
        'Billing: people plan term length': peoplePlan.termLengthInMonths,
        'Billing: paid plan types': projectAccount.paidPlanTypes,
        'Billing: events/MTU plan ARR': projectAccount.eventsPlanArr || 0,
        'Billing: people plan ARR': projectAccount.peoplePlanArr || 0,
        'Billing: total account ARR': projectAccount.arr || 0,
        'Billing: account status': projectAccount.arr > 0 ? `paid` : `unpaid`,
        'Billing: MTU add-on packages': projectAccount.packages,
        'Billing: services plan ARR': projectAccount.servicesArr || 0,
        'Billing: credit IDs': billingCredit.ids,
        'Billing: credit names': billingCredit.names,
        'Billing: credit balance': billingCredit.balance,
      });

      // Org properties
      mpSdkSetBeforeSendProps({
        'Organization ID': organization.id,
        organization_id: organization.id,
        'Organization Name': organization.name,
        'Organization User Count': organization.userCount,
      });

      const workspaceProps = projectProps.workspace;
      if (workspaceProps) {
        mpSdkSetBeforeSendProps({
          workspace_id: workspaceProps.id,
          'Is Workspace Global': workspaceProps.isGlobal,
          'Is Workspace Default': workspaceProps.isDefault,
        });
      } else {
        mixpanel.unregister(`workspace_id`);
        mixpanel.unregister(`Is Workspace Global`);
        mixpanel.unregister(`Is Workspace Default`);
      }

      setTrackingFeatureFlagProps();
      mpSdkSetBeforeSendProps({
        feature_gate_name: projectProps.featureGateName,
        'Project Role': user.role,
      });

      mpSdkSetBeforeSendProps({
        has_integrated_arb: projectProps.hasIntegratedArb,
        has_integrated_engage: projectProps.hasIntegratedEngage,
        has_scd_sync: projectProps.hasScdSync,
        has_integrated_session_replay: projectProps.hasIntegratedSessionReplay,
        'events tally for project': projectProps.tallies.events,
        'people tally for project': projectProps.tallies.people,
        'mtu tally for project': projectProps.tallies.mtu,
        'project user count': projectProps.userCount,
      });
    }

    setEmbedTrackingProps();
  } catch (err) {
    reporter.error(`Error fetching tracking props`, err);
  }

  mixpanel.start_batch_senders();
}
